import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import DashboardCard from '@/app/fuse-layouts/layout1/components/Dashboard/DashboardCard';
import GradientButton from '@shared/components/GradientButton';
import { formatMoney } from '@shared/helpers/format';
import { getBillingInvoiceSchoolSummary } from 'billing/services';
import { getConnectStatus } from 'billing/services/stripeConnect';
import { unwrap } from 'billing/services/unwrap';

/**
 * The home dashboard's billing presence: Total Outstanding, Failed Payments
 * and Auto-Pay Enrolled, each landing on the matching Billing Summary tab.
 * Drawn only when the school has billing on and the admin holds the Billing
 * permission — the same two gates the sidebar link uses. While Stripe setup
 * is incomplete the card asks for that instead of showing metrics.
 */
const Metric = ({ label, value, sub, subClass = 'text-gray-500', onClick }) => (
	<button
		type="button"
		onClick={onClick}
		className="flex flex-col items-start gap-4 bg-transparent border-0 p-0 cursor-pointer text-left hover:opacity-75"
	>
		<span className="text-13 text-gray-600">{label}</span>
		<span className="text-24 font-bold text-pd-black leading-none">{value}</span>
		{sub ? <span className={`text-12 ${subClass}`}>{sub}</span> : null}
	</button>
);

const BillingWidget = () => {
	const navigate = useNavigate();
	const billingEnabled = useSelector(({ auth }) => (auth?.user?.school ?? auth?.user?.data?.school)?.billing_enabled);
	// Same rule as the route guard: no permission list at all means an owner
	// or full admin, who is admitted; a list without Billing is not.
	const canBilling = useSelector(({ auth }) => {
		const permissions = auth?.user?.permissions ?? auth?.user?.data?.permissions;
		return permissions == null || permissions.some((p) => p?.replace(/^\/+/, '') === 'Billing');
	});
	const [summary, setSummary] = useState(null);
	const [ready, setReady] = useState(null);
	const [failed, setFailed] = useState(false);

	useEffect(() => {
		if (!billingEnabled || !canBilling) return undefined;
		let cancelled = false;
		getConnectStatus()
			.then((res) => {
				if (cancelled) return null;
				const status = unwrap(res);
				setReady(!!status?.all_gates_passed);
				return status?.all_gates_passed ? getBillingInvoiceSchoolSummary() : null;
			})
			.then((res) => {
				if (!cancelled && res) setSummary(res.data?.data ?? null);
			})
			.catch(() => {
				if (!cancelled) setFailed(true);
			});
		return () => {
			cancelled = true;
		};
	}, [billingEnabled, canBilling]);

	if (!billingEnabled || !canBilling) return null;

	const failedCount = summary?.failed_payment_count ?? 0;
	const autopayPct = summary?.students_total
		? Math.round(((summary.autopay_enrolled_count ?? 0) / summary.students_total) * 100)
		: 0;

	let body;
	if (failed) {
		body = <p className="text-14 text-error m-0">Couldn&rsquo;t load billing figures.</p>;
	} else if (ready === null || (ready && !summary)) {
		body = <p className="text-14 text-gray-500 m-0">Loading…</p>;
	} else if (!ready) {
		body = (
			<div className="flex items-center justify-between gap-16 flex-wrap">
				<p className="text-14 text-gray-700 m-0">
					Billing is on for your school, but Stripe setup isn&rsquo;t finished yet — families can&rsquo;t be
					invoiced until it is.
				</p>
				<GradientButton size="small" variant="light-blue" onClick={() => navigate('/billing/onboarding')}>
					Finish Stripe setup
				</GradientButton>
			</div>
		);
	} else {
		body = (
			<div className="grid grid-cols-1 sm:grid-cols-3 gap-24">
				<Metric
					label="Total Outstanding"
					value={formatMoney(summary.outstanding_cents ?? 0, summary.currency)}
					sub={(summary.past_due_cents ?? 0) > 0 ? `${formatMoney(summary.past_due_cents, summary.currency)} past due` : null}
					subClass="text-error"
					onClick={() => navigate('/billing?tab=unpaid')}
				/>
				<Metric
					label="Failed Payments"
					value={failedCount}
					sub={failedCount > 0 ? 'Requires attention' : 'All caught up'}
					subClass={failedCount > 0 ? 'text-error' : 'text-gray-500'}
					onClick={() => navigate('/billing?tab=failed')}
				/>
				<Metric
					label="Auto-Pay Enrolled"
					value={`${autopayPct}%`}
					sub={`${summary.autopay_enrolled_count ?? 0} of ${summary.students_total ?? 0} students`}
					onClick={() => navigate('/billing?tab=autopay')}
				/>
			</div>
		);
	}

	return (
		<DashboardCard title="Billing" showViewMore onViewMore={() => navigate('/billing')}>
			{body}
		</DashboardCard>
	);
};

export default BillingWidget;
